(() => {
  'use strict';

  const form = document.getElementById('cart');
  const dataNode = document.querySelector('[data-sassy-hive-cart-lines]');
  if (!form || !dataNode) return;

  const root = window.Shopify?.routes?.root || '/';
  const urls = {
    cart: `${root}cart.js`.replace(/\/{2,}/g, '/'),
    add: `${root}cart/add.js`.replace(/\/{2,}/g, '/'),
    change: `${root}cart/change.js`.replace(/\/{2,}/g, '/'),
    update: `${root}cart/update.js`.replace(/\/{2,}/g, '/'),
    checkout: `${root}checkout`.replace(/\/{2,}/g, '/'),
    tiers: window.SassyHiveTierUrl || `${root}#shop`
  };
  const draftKeys = ['sassyHiveQuizDraftV1', 'sassyHiveEditCustomizationV2'];
  const statusNode = document.querySelector('[data-cart-controller-status]');
  let busy = false;

  const parseLines = () => {
    try {
      const parsed = JSON.parse(dataNode.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('The Sassy Hive cart data could not be read.', error);
      return [];
    }
  };

  const lines = parseLines();
  const addonLimit = line => ({ '42443754995767': 10, '42443755028535': 5 })[String(line.variantId || line.variant_id)] || 0;
  const standaloneLimit = line => ({ '67590098452535': 10, '67590098485303': 5 })[String(line.variantId || line.variant_id)] || 0;
  const byKey = new Map(lines.map((line) => [line.key, line]));

  const propertiesOf = (line) => line?.properties || {};
  const pairIdOf = (line) => String(
    line?.pairId ||
    propertiesOf(line)['_Customization ID'] ||
    propertiesOf(line)['_tsh_customization_id'] ||
    propertiesOf(line)['_customization_id'] ||
    ''
  ).trim();

  const isBox = (line) => Boolean(
    line?.isMysteryBox ||
    String(line?.variantId || line?.variant_id) === '67591769030711' ||
    propertiesOf(line)['Mystery Charms'] ||
    propertiesOf(line)['Mystery Capsules'] ||
    /^\d+\s*mystery\s*(box|charm|capsule)/i.test(line?.productTitle || '')
  );

  const isParent = (line) => isBox(line) || Boolean(standaloneLimit(line));

  const isVideo = (line) => Boolean(
    line?.isFeatureVideo ||
    propertiesOf(line)['For Mystery Box'] ||
    /featured?\s*(mystery\s*box\s*)?video/i.test(line?.productTitle || '')
  );

  const findPair = (line, predicate) => {
    const pairId = pairIdOf(line);
    if (!pairId) return null;
    return lines.find((candidate) =>
      candidate.key !== line.key &&
      pairIdOf(candidate) === pairId &&
      predicate(candidate)
    ) || null;
  };

  const setStatus = (message, type = 'info') => {
    if (!statusNode) return;
    statusNode.hidden = !message;
    statusNode.textContent = message || '';
    statusNode.dataset.statusType = type;
  };

  const request = async (url, body, timeoutMs = 15000) => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body),
        signal: controller.signal
      });

      const text = await response.text();
      let payload = {};
      try { payload = text ? JSON.parse(text) : {}; } catch (_) {}

      if (!response.ok) {
        throw new Error(payload.description || payload.message || `Shopify returned ${response.status}.`);
      }
      return payload;
    } finally {
      window.clearTimeout(timeout);
    }
  };

  const getCart = async () => {
    const response = await fetch(urls.cart, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('The cart could not be checked.');
    return response.json();
  };

  const setBusy = (active, button, busyText = 'Updating…') => {
    busy = active;
    form.setAttribute('aria-busy', String(active));
    form.querySelectorAll('button, [data-cart-action]').forEach((control) => {
      if (active) {
        control.dataset.wasDisabled = control.disabled ? 'true' : 'false';
        if ('disabled' in control) control.disabled = true;
        control.setAttribute('aria-disabled', 'true');
      } else {
        if ('disabled' in control && control.dataset.wasDisabled !== 'true') control.disabled = false;
        control.removeAttribute('aria-disabled');
        delete control.dataset.wasDisabled;
      }
    });

    if (button) {
      if (active) {
        button.dataset.originalText = button.textContent;
        button.textContent = busyText;
      } else if (button.dataset.originalText) {
        button.textContent = button.dataset.originalText;
        delete button.dataset.originalText;
      }
    }
  };

  const reload = () => window.location.reload();

  const cleanVideoFields = (properties) => {
    const next = { ...(properties || {}) };
    next['Feature Video'] = 'No';
    delete next['Video Identification Choice'];
    delete next['Video Identification'];
    delete next['Video Consent'];
    delete next['Video Consent Timestamp'];
    delete next['Video Consent Version'];
    return next;
  };

  const ensureVideoFields = (properties, videoLine) => {
    const next = { ...(properties || {}) };
    const videoProps = propertiesOf(videoLine);
    next['Feature Video'] = 'Yes';
    if (videoProps['Video Identification']) next['Video Identification'] = videoProps['Video Identification'];
    if (videoProps['Consent'] === 'Confirmed') next['Video Consent'] = 'Confirmed';
    return next;
  };

  const cloneProperties = (properties, newPairId) => {
    const next = { ...(properties || {}) };
    let found = false;
    Object.keys(next).forEach((key) => {
      if (/customi[sz]ation.*id|pair.*id|box.*id/i.test(key)) {
        next[key] = newPairId;
        found = true;
      }
    });
    if (!found) next['_Customization ID'] = newPairId;
    return next;
  };

  const createPairId = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `sassy-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  };

  const removeBoxAndVideo = async (boxLine, button) => {
    if (busy) return;
    const videoLine = findPair(boxLine, isVideo);
    const updates = { [boxLine.key]: 0 };
    if (videoLine) updates[videoLine.key] = 0;
    lines.filter(line => addonLimit(line) && pairIdOf(line) === pairIdOf(boxLine)).forEach(line => { updates[line.key] = 0; });

    setBusy(true, button, videoLine ? 'Removing box + video…' : 'Removing box…');
    setStatus(videoLine ? 'Removing the Mystery Box and its Feature Video…' : 'Removing the Mystery Box…');
    try {
      await request(urls.update, { updates });
      reload();
    } catch (error) {
      setStatus(error.message, 'error');
      setBusy(false, button);
    }
  };

  const removeVideoAndUpdateBox = async (videoLine, button) => {
    if (busy) return;
    const boxLine = findPair(videoLine, isParent);
    setBusy(true, button, 'Removing video…');
    setStatus('Removing the Feature Video and updating the connected Mystery Box…');

    if (!boxLine) {
      try {
        await request(urls.update, { updates: { [videoLine.key]: 0 } });
        reload();
      } catch (error) {
        setStatus(error.message, 'error');
        setBusy(false, button);
      }
      return;
    }

    const originalProperties = { ...propertiesOf(boxLine) };
    const revisedProperties = cleanVideoFields(originalProperties);

    try {
      const changedCart = await request(urls.change, {
        id: boxLine.key,
        quantity: standaloneLimit(boxLine) ? boxLine.quantity : 1,
        properties: revisedProperties
      });

      try {
        await request(urls.change, { id: videoLine.key, quantity: 0 });
      } catch (removeError) {
        try {
          const currentBox = (changedCart.items || []).find((item) =>
            String(item.properties?.['_Customization ID'] || '') === pairIdOf(boxLine) &&
            isParent(item)
          );
          await request(urls.change, {
            id: currentBox?.key || boxLine.key,
            quantity: standaloneLimit(boxLine) ? boxLine.quantity : 1,
            properties: originalProperties
          });
        } catch (rollbackError) {
          console.error('Feature Video rollback failed.', rollbackError);
        }
        throw removeError;
      }
      reload();
    } catch (error) {
      setStatus(`${error.message} The cart was rechecked to avoid a partial change.`, 'error');
      setBusy(false, button);
      window.setTimeout(reload, 1200);
    }
  };

  const duplicateBox = async (boxLine, button) => {
    if (busy) return;
    const newPairId = createPairId();
    const videoLine = findPair(boxLine, isVideo);
    const items = [{ id: boxLine.variantId, quantity: 1, properties: cloneProperties(propertiesOf(boxLine), newPairId) }];
    if (videoLine) items.push({ id: videoLine.variantId, quantity: 1, properties: cloneProperties(propertiesOf(videoLine), newPairId) });
    lines.filter(line => addonLimit(line) && pairIdOf(line) === pairIdOf(boxLine)).forEach(line => items.push({ id: line.variantId, quantity: Math.min(line.quantity, addonLimit(line)), properties: cloneProperties(propertiesOf(line), newPairId) }));

    setBusy(true, button, 'Duplicating…');
    setStatus(videoLine
      ? 'Creating a separate Mystery Box and matching Feature Video with the same answers…'
      : 'Creating a separate Mystery Box with the same answers…');

    try {
      await request(urls.add, { items });
      reload();
    } catch (error) {
      setStatus(error.message, 'error');
      setBusy(false, button);
    }
  };

  const startSeparateBox = () => {
    draftKeys.forEach((key) => {
      try { sessionStorage.removeItem(key); } catch (_) {}
    });
    const target = new URL(urls.tiers, window.location.origin);
    target.searchParams.set('new_box', '1');
    target.hash = 'shop';
    window.location.assign(`${target.pathname}${target.search}${target.hash}`);
  };

  const removeRegularLine = async (line, button) => {
    if (busy) return;
    setBusy(true, button, 'Removing…');
    try {
      const updates = { [line.key]: 0 };
      if (standaloneLimit(line)) {
        const video = findPair(line, isVideo);
        if (video) updates[video.key] = 0;
      }
      await request(urls.update, { updates });
      reload();
    } catch (error) {
      setStatus(error.message, 'error');
      setBusy(false, button);
    }
  };

  const currentNote = () => form.querySelector('[name="note"]')?.value || '';

  const requestedUpdates = () => {
    const updates = {};
    const standaloneCounts = new Map();
    lines.forEach((line) => {
      if (isBox(line) || isVideo(line)) {
        updates[line.key] = 1;
        return;
      }
      const article = [...form.querySelectorAll('[data-line-key]')].find((node) => node.dataset.lineKey === line.key);
      const input = article?.querySelector('input[name="updates[]"]');
      const quantity = Number.parseInt(input?.value, 10);
      const requested = Number.isFinite(quantity) && quantity >= 0 ? quantity : line.quantity;
      const standaloneCap = standaloneLimit(line);
      if (standaloneCap) {
        const bucket = String(line.variantId || line.variant_id);
        const used = standaloneCounts.get(bucket) || 0;
        const allowed = Math.min(requested, Math.max(0, standaloneCap - used));
        updates[line.key] = allowed;
        standaloneCounts.set(bucket, used + allowed);
      } else {
        updates[line.key] = Math.min(requested, addonLimit(line) || Infinity);
      }
    });
    return updates;
  };

  const normalizeFreshCart = async () => {
    const cart = await getCart();
    const items = cart.items || [];
    const getPair = (item) => String(
      item.properties?.['_Customization ID'] ||
      item.properties?.['_tsh_customization_id'] ||
      item.properties?.['_customization_id'] ||
      ''
    ).trim();
    const itemIsBox = (item) => Boolean(
      String(item.variant_id) === '67591769030711' ||
      item.properties?.['Mystery Charms'] ||
      item.properties?.['Mystery Capsules'] ||
      /^\d+\s*mystery\s*(box|charm|capsule)/i.test(item.product_title || item.title || '')
    );
    const itemIsVideo = (item) => Boolean(
      item.properties?.['For Mystery Box'] ||
      /featured?\s*(mystery\s*box\s*)?video/i.test(item.product_title || item.title || '')
    );

    const boxesByPair = new Map();
    const videosByPair = new Map();
    items.forEach((item) => {
      const pairId = getPair(item);
      if (!pairId) return;
      if (itemIsBox(item) || standaloneLimit(item)) boxesByPair.set(pairId, item);
      if (itemIsVideo(item)) videosByPair.set(pairId, item);
    });

    const updates = {};
    const addonCounts = new Map();
    const standaloneCounts = new Map();
    items.forEach((item) => {
      const pairId = getPair(item);
      const standaloneCap = standaloneLimit(item);
      if (standaloneCap) {
        const bucket = String(item.variant_id);
        const used = standaloneCounts.get(bucket) || 0;
        const allowed = Math.min(item.quantity, Math.max(0, standaloneCap - used));
        if (allowed !== item.quantity) updates[item.key] = allowed;
        standaloneCounts.set(bucket, used + allowed);
      }
      const limit = addonLimit(item);
      if (limit) {
        const bucket = `${pairId}:${item.variant_id}`;
        const used = addonCounts.get(bucket) || 0;
        const allowed = pairId && boxesByPair.has(pairId) ? Math.min(item.quantity, Math.max(0, limit - used)) : 0;
        if (allowed !== item.quantity) updates[item.key] = allowed;
        addonCounts.set(bucket, used + allowed);
      }
      if ((itemIsBox(item) || itemIsVideo(item)) && item.quantity !== 1) updates[item.key] = 1;
      if (itemIsVideo(item) && pairId && !boxesByPair.has(pairId)) updates[item.key] = 0;
    });

    if (Object.keys(updates).length) {
      await request(urls.update, { updates });
      return { changed: true, cart };
    }

    for (const [pairId, box] of boxesByPair) {
      const video = videosByPair.get(pairId);
      const claimsVideo = box.properties?.['Feature Video'] === 'Yes';
      if (claimsVideo && !video) {
        await request(urls.change, { id: box.key, quantity: standaloneLimit(box) ? box.quantity : 1, properties: cleanVideoFields(box.properties) });
        return { changed: true, cart };
      }
      if (!claimsVideo && video) {
        await request(urls.change, { id: box.key, quantity: standaloneLimit(box) ? box.quantity : 1, properties: ensureVideoFields(box.properties, video) });
        return { changed: true, cart };
      }
    }

    return { changed: false, cart };
  };

  const updateCart = async (button) => {
    if (busy) return;
    setBusy(true, button, 'Updating cart…');
    setStatus('Updating your cart…');
    try {
      await request(urls.update, { updates: requestedUpdates(), note: currentNote() });
      const result = await normalizeFreshCart();
      if (result.changed) setStatus('The cart was corrected to keep each customization and Feature Video together.');
      reload();
    } catch (error) {
      setStatus(error.message, 'error');
      setBusy(false, button);
    }
  };

  const proceedToCheckout = async (button) => {
    if (busy) return;
    setBusy(true, button, 'Checking cart…');
    setStatus('Checking each Mystery Box and Feature Video before checkout…');
    try {
      await request(urls.update, { updates: requestedUpdates(), note: currentNote() });
      let result = await normalizeFreshCart();
      if (result.changed) result = await normalizeFreshCart();
      if (result.changed) throw new Error('The cart needed more than one correction. Please review it once more before checkout.');
      window.location.assign(urls.checkout);
    } catch (error) {
      setStatus(error.message, 'error');
      setBusy(false, button);
      window.setTimeout(reload, 1400);
    }
  };

  const handleAction = (event) => {
    const control = event.target.closest('[data-cart-action]');
    if (!control || !form.contains(control)) return;
    event.preventDefault();
    if (busy) return;
    const line = byKey.get(control.dataset.lineKey || '');
    switch (control.dataset.cartAction) {
      case 'remove-box': if (line) removeBoxAndVideo(line, control); break;
      case 'remove-video': if (line) removeVideoAndUpdateBox(line, control); break;
      case 'remove-line': if (line) removeRegularLine(line, control); break;
      case 'duplicate-box': if (line) duplicateBox(line, control); break;
      case 'new-box': startSeparateBox(); break;
      default: break;
    }
  };

  const handleSubmit = (event) => {
    if (event.target !== form) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const submitter = event.submitter;
    if (submitter?.name === 'checkout') proceedToCheckout(submitter);
    else updateCart(submitter || form.querySelector('[name="update"]'));
  };

  document.addEventListener('click', handleAction, true);
  form.addEventListener('submit', handleSubmit, true);

  normalizeFreshCart()
    .then((result) => {
      if (result.changed) {
        setStatus('The cart was corrected to keep each personalized item properly paired.');
        window.setTimeout(reload, 500);
      }
    })
    .catch((error) => {
      console.error('The Sassy Hive cart integrity check failed.', error);
      setStatus('We could not complete the automatic cart check. Please refresh before checkout.', 'error');
    });
})();

